/**
 * Login Page
 *
 * Email/password login with social login options.
 * Handles NEW_PASSWORD_REQUIRED challenge inline (no navigation).
 * Blueprint Task: 2.2.4
 */

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { BrandWordmark } from '../components/shared/BrandWordmark';
import { SocialLoginButtons } from '../components/SocialLoginButtons';
import { PORTAL_BRAND } from '../config/branding';
import { useAuth } from '../providers/AuthProvider';
import { linkGuestOrders } from '../services/orderService';

function validateNewPassword(password: string): string | null {
  if (password.length < 8) return 'Password must be at least 8 characters';
  if (!/[A-Z]/.test(password)) return 'Password must contain an uppercase letter';
  if (!/[a-z]/.test(password)) return 'Password must contain a lowercase letter';
  if (!/[0-9]/.test(password)) return 'Password must contain a number';
  return null;
}

/**
 *
 */
export function Login({ skipGuestLink, isAdmin }: { skipGuestLink?: boolean; isAdmin?: boolean } = {}) {
  const { login, completeNewPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl') ?? '/dashboard';

  const orderId = searchParams.get('orderId') ?? undefined;
  const justVerified = searchParams.get('verified') === '1';
  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // New password challenge state
  const [mode, setMode] = useState<'login' | 'newPassword'>('login');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  async function handleLogin(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await login(email, password);
      if (!skipGuestLink) {
        try {
          const result = await linkGuestOrders(email, orderId);
          if (result.linkedCount > 0) {
            console.info(`Linked ${result.linkedCount} guest order(s) to account`);
          }
        } catch (linkError) {
          console.warn('Failed to link guest orders:', linkError);
        }
      }
      void navigate(decodeURIComponent(returnUrl), { replace: true });
    } catch (err) {
      const error = err as { message?: string; code?: string };
      if (error.code === 'NewPasswordRequired') {
        setMode('newPassword');
        setPassword('');
      } else if (error.code === 'UserNotConfirmedException') {
        setError('Please verify your email before logging in.');
      } else if (error.code === 'NotAuthorizedException') {
        setError('Invalid email or password.');
      } else {
        setError(error.message ?? 'An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleNewPassword(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const validationError = validateNewPassword(newPassword);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      await completeNewPassword(newPassword);
      if (!skipGuestLink) {
        try {
          const result = await linkGuestOrders(email, orderId);
          if (result.linkedCount > 0) {
            console.info(`Linked ${result.linkedCount} guest order(s) to account`);
          }
        } catch (linkError) {
          console.warn('Failed to link guest orders:', linkError);
        }
      }
      void navigate(decodeURIComponent(returnUrl), { replace: true });
    } catch (err) {
      const error = err as { message?: string; code?: string };
      setError(error.message ?? 'Failed to set password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <a href={PORTAL_BRAND.websiteUrl} className="flex justify-center items-center gap-2">
          <img src={PORTAL_BRAND.logoMarkUrl} alt="" className="h-8 w-8 rounded-lg" />
          <span className="text-xl font-bold tracking-tight" style={{ color: 'hsl(var(--color-primary-700))' }}><BrandWordmark /></span>
        </a>
        <a
          href={PORTAL_BRAND.websiteUrl}
          className="mt-1 block text-center text-sm"
          style={{ color: 'hsl(var(--color-neutral-400))' }}
        >
          &larr; {PORTAL_BRAND.websiteLabel}
        </a>
        <h2
          className="mt-6 text-center text-2xl font-heading font-medium tracking-tight"
          style={{ color: 'hsl(var(--color-neutral-900))' }}
        >
          {mode === 'login' ? 'Sign in to your account' : 'Set your new password'}
        </h2>
        <p
          className="mt-2 text-center text-sm"
          style={{ color: 'hsl(var(--color-neutral-600))' }}
        >
          {mode === 'login' ? (
            isAdmin ? null : (
              <>
                Or{' '}
                <Link
                  to="/signup"
                  className="font-medium underline"
                  style={{ color: 'hsl(var(--color-primary-600))' }}
                >
                  create a new account
                </Link>
              </>
            )
          ) : (
            `Choose a password for your ${PORTAL_BRAND.companyName} account`
          )}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div
          className="rounded-lg px-4 py-8 shadow sm:px-10"
          style={{ backgroundColor: 'hsl(var(--color-neutral-50))' }}
        >
          {justVerified && !error && (
            <div
              className="mb-4 rounded-md p-3 text-sm"
              style={{
                backgroundColor: 'hsl(var(--color-success-50))',
                color: 'hsl(var(--color-success-700))',
              }}
              role="status"
            >
              Email verified! Sign in with the password you created during registration.
            </div>
          )}

          {error && (
            <div
              className="mb-4 rounded-md p-3 text-sm"
              style={{
                backgroundColor: 'hsl(var(--color-error-50))',
                color: 'hsl(var(--color-error-700))',
              }}
              role="alert"
            >
              {error}
            </div>
          )}

          {mode === 'login' ? (
            <form className="space-y-6" onSubmit={handleLogin}>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium"
                  style={{ color: 'hsl(var(--color-neutral-700))' }}
                >
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full appearance-none rounded-md border px-3 py-2 placeholder-brand-neutral-400 shadow-sm focus:outline-hidden focus:ring-2 sm:text-sm"
                    style={{
                      borderColor: 'hsl(var(--color-neutral-300))',
                    }}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium"
                  style={{ color: 'hsl(var(--color-neutral-700))' }}
                >
                  Password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full appearance-none rounded-md border px-3 py-2 placeholder-brand-neutral-400 shadow-sm focus:outline-hidden focus:ring-2 sm:text-sm"
                    style={{
                      borderColor: 'hsl(var(--color-neutral-300))',
                    }}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 rounded border-brand-neutral-300"
                    style={{ accentColor: 'hsl(var(--color-primary-600))' }}
                  />
                  <label
                    htmlFor="remember-me"
                    className="ml-2 block text-sm"
                    style={{ color: 'hsl(var(--color-neutral-700))' }}
                  >
                    Remember me
                  </label>
                </div>

                <div className="text-sm">
                  <Link
                    to="/forgot-password"
                    className="font-medium"
                    style={{ color: 'hsl(var(--color-primary-600))' }}
                  >
                    Forgot your password?
                  </Link>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex w-full justify-center rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus:outline-hidden focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
                  style={{
                    backgroundColor: 'hsl(var(--color-primary-600))',
                  }}
                >
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </button>
              </div>
            </form>
          ) : (
            <form className="space-y-6" onSubmit={handleNewPassword}>
              <div>
                <label
                  className="block text-sm font-medium"
                  style={{ color: 'hsl(var(--color-neutral-700))' }}
                >
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    type="email"
                    value={email}
                    readOnly
                    className="block w-full appearance-none rounded-md border px-3 py-2 shadow-sm sm:text-sm"
                    style={{
                      borderColor: 'hsl(var(--color-neutral-200))',
                      backgroundColor: 'hsl(var(--color-neutral-50))',
                      color: 'hsl(var(--color-neutral-500))',
                    }}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="new-password"
                  className="block text-sm font-medium"
                  style={{ color: 'hsl(var(--color-neutral-700))' }}
                >
                  New password
                </label>
                <div className="mt-1">
                  <input
                    id="new-password"
                    name="new-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="block w-full appearance-none rounded-md border px-3 py-2 placeholder-brand-neutral-400 shadow-sm focus:outline-hidden focus:ring-2 sm:text-sm"
                    style={{
                      borderColor: 'hsl(var(--color-neutral-300))',
                    }}
                  />
                </div>
                <p
                  className="mt-1.5 text-xs"
                  style={{ color: 'hsl(var(--color-neutral-500))' }}
                >
                  At least 8 characters with uppercase, lowercase, and a number
                </p>
              </div>

              <div>
                <label
                  htmlFor="confirm-password"
                  className="block text-sm font-medium"
                  style={{ color: 'hsl(var(--color-neutral-700))' }}
                >
                  Confirm password
                </label>
                <div className="mt-1">
                  <input
                    id="confirm-password"
                    name="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="block w-full appearance-none rounded-md border px-3 py-2 placeholder-brand-neutral-400 shadow-sm focus:outline-hidden focus:ring-2 sm:text-sm"
                    style={{
                      borderColor: 'hsl(var(--color-neutral-300))',
                    }}
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex w-full justify-center rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus:outline-hidden focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
                  style={{
                    backgroundColor: 'hsl(var(--color-primary-600))',
                  }}
                >
                  {isLoading ? 'Setting password...' : 'Set Password'}
                </button>
              </div>
            </form>
          )}

          {mode === 'login' && !isAdmin && <SocialLoginButtons />}
        </div>
      </div>
    </div>
  );
}
